<script>
import { buildTrackingGetCurl, buildCurlFromAxiosError } from '../utils/buildCurlCommand';

function decodeBase64Utf8(value) {
  try {
    return decodeURIComponent(escape(window.atob(value)));
  } catch (e) {
    return null;
  }
}

function filenameFromResponse(response, fallback) {
  const header = response && response.headers && response.headers['content-disposition'];
  const match = header && header.match(/filename="?([^";]+)"?/);
  return match ? match[1] : fallback;
}

function extractErrorMessage(error) {
  const data = error && error.response && error.response.data;
  if (data && typeof data.message === 'string') {
    return data.message;
  }
  if (Array.isArray(data) && data.length) {
    return String(data[0]);
  }
  if (error && error.message) {
    return error.message;
  }
  return 'Unknown error';
}

const OnboardingTrackingService = {};

OnboardingTrackingService.install = function (Vue) {
  const service = {};
  const loadingState = Vue.observable({ active: false, pending: 0 });
  service.loadingState = loadingState;

  const trackRequest = (promise) => {
    loadingState.pending += 1;
    loadingState.active = true;
    return promise.finally(() => {
      loadingState.pending = Math.max(0, loadingState.pending - 1);
      loadingState.active = loadingState.pending > 0;
    });
  };

  Object.defineProperty(Vue.prototype, '$onboardingTrackingService', { value: service });

  const buildQuery = (params = {}) => {
    const parts = [];
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (value === undefined || value === null || value === '') {
        return;
      }
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
    });
    return parts.length ? ('?' + parts.join('&')) : '';
  };

  const request = (executor, options = {}) => {
    const promise = executor().catch((error) => {
      service.handleError(error, options);
      return Promise.reject(error);
    });
    return options.silent ? promise : trackRequest(promise);
  };

  service.getSummary = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-summary' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getFunnel = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-funnel' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getSessions = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-sessions' + buildQuery(params))
      .then((response) => response.data));
  };

  /** payload: { called: boolean, note: string } — returns the updated journey row. */
  service.updateSessionCall = function (sessionId, payload) {
    return request(() => Vue.axios.put('/api/admin/onboarding-sessions/' + encodeURIComponent(sessionId) + '/call', payload)
      .then((response) => response.data));
  };

  // Pass { silent: true } for optional panels: no loading overlay, no error toast.
  // ---- GPS AI (Claude). GET = saved answer only; run = one Claude call, saved for every admin.
  service.getGpsAiStatus = function () {
    return request(() => Vue.axios.get('/api/admin/gps-ai/status').then((response) => response.data), { silent: true });
  };

  service.getGpsAi = function (feature, params = {}) {
    return request(() => Vue.axios.get('/api/admin/gps-ai/' + feature + buildQuery(params))
      .then((response) => response.data), { silent: true });
  };

  service.runGpsAi = function (feature, params = {}) {
    return Vue.axios.post('/api/admin/gps-ai/' + feature + '/run' + buildQuery(params))
      .then((response) => response.data);
  };

  service.getDailyBookings = function (params = {}, options = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-daily-bookings' + buildQuery(params))
      .then((response) => response.data), options);
  };

  // Pass { silent: true } for optional panels: no loading overlay, no error toast.
  service.getStopReasons = function (params = {}, options = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-stop-reasons' + buildQuery(params))
      .then((response) => response.data), options);
  };

  service.getSession = function (sessionId, params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-sessions/' + encodeURIComponent(sessionId) + buildQuery(params))
      .then((response) => response.data));
  };

  service.getTraffic = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-traffic' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getErrors = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-errors' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getOtpProblems = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-otp-problems' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getApis = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-apis' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getCapacity = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-capacity' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getSupport = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-support' + buildQuery(params))
      .then((response) => response.data));
  };

  service.importSupport = function (payload) {
    return request(() => Vue.axios.post('/api/admin/onboarding-support/import', payload)
      .then((response) => response.data));
  };

  service.getIssues = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-issues' + buildQuery(params))
      .then((response) => response.data));
  };

  service.getAlertSettings = function () {
    return request(() => Vue.axios.get('/api/admin/onboarding-alert-settings')
      .then((response) => response.data));
  };

  service.updateAlertSettings = function (payload) {
    return request(() => Vue.axios.put('/api/admin/onboarding-alert-settings', payload)
      .then((response) => response.data));
  };

  service.sendAlertTest = function () {
    return request(() => Vue.axios.post('/api/admin/onboarding-alert-settings/test')
      .then((response) => response.data));
  };

  service.createChangeAnnotation = function (payload) {
    return request(() => Vue.axios.post('/api/admin/onboarding-change-annotations', payload)
      .then((response) => response.data));
  };

  service.getWeeklyReport = function (params = {}) {
    return request(() => Vue.axios.get('/api/admin/onboarding-weekly-report' + buildQuery(params))
      .then((response) => response.data));
  };

  service.askQuestion = function (payload) {
    return request(() => Vue.axios.post('/api/admin/onboarding-chat', payload)
      .then((response) => response.data), { silent: true });
  };

  service.downloadChatExport = function (fileId, filename) {
    return request(() => Vue.axios.get('/api/admin/onboarding-chat/exports/' + encodeURIComponent(fileId), {
      responseType: 'blob'
    }).then((response) => {
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      if (filename) {
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return null;
      }
      return url;
    }), { silent: true });
  };

  service.exportSessions = function (params = {}) {
    const query = buildQuery(params);
    return request(() => Vue.axios.get('/api/admin/onboarding-export' + query, { responseType: 'blob' })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'onboarding-export.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }));
  };

  /**
   * Full ZIP export: the filtered journeys, every step inside them, and the datasets
   * behind each tracking page. Can take a while on wide date ranges, so callers should
   * await the promise and keep the button busy until it settles.
   */
  service.exportSessionsBundle = function (params = {}) {
    const query = buildQuery(params);
    return request(() => Vue.axios.get('/api/admin/onboarding-export-bundle' + query, { responseType: 'blob' })
      .then((response) => {
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filenameFromResponse(response, 'journeys-full-export.zip');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }));
  };

  service.exportJourney = function (sessionId) {
    return request(() => Vue.axios.get(
      '/api/admin/onboarding-sessions/' + encodeURIComponent(sessionId) + '/export',
      { responseType: 'blob' }
    ).then((response) => {
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'journey-' + sessionId + '.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }));
  };

  service.handleError = function (error, options = {}) {
    if (error && error.response) {
      console.debug(error.response.data);
      console.debug(error.response.status);
    } else if (error && error.request) {
      console.debug(error.request);
    } else if (error) {
      console.debug('Error', error.message);
    }

    const headerCurl = error && error.response && error.response.headers
      && error.response.headers['x-debug-curl'];
    const curl = (headerCurl && decodeBase64Utf8(headerCurl))
      || buildCurlFromAxiosError(error, null, Vue.axios.defaults);
    service.lastErrorCurl = curl || null;

    if (curl) {
      // eslint-disable-next-line no-console
      console.error('[onboarding-tracking] Request failed — reproduce with:\n' + curl);
    }

    if (!options.silent && Vue.prototype.$awn) {
      try {
        Vue.prototype.$awn.alert(
          'Tracking API error: ' + extractErrorMessage(error)
          + (curl ? ' (curl reproduction logged to the browser console)' : '')
        );
      } catch (notifyError) {
        console.debug('Failed to show tracking error notification', notifyError);
      }
    }
  };

  service.buildFailedApiCurl = function (path, params) {
    return buildTrackingGetCurl(path, params, Vue.axios.defaults);
  };
};

export default OnboardingTrackingService;
</script>
